import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PAYMENT, RIDE_TYPES, money, when } from "@/lib/rides";
import { StatusBadge, Stops } from "@/components/RideStatus";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3 text-xs"><span className="text-muted-foreground">{k}</span><span className="text-right font-mono">{v ?? "—"}</span></div>
);

function Pricing({ q }) {
  if (!q || q.status !== "ok") return <Row k="Pricing" v={q?.reason || "ADMIN QUOTE REQUIRED / route unavailable"} />;
  const rec = q.recurring || {};
  return (
    <>
      <Row k="Pricing version" v={`v${q.pricing_version} · ${q.vehicle_label}${q.trip_type ? ` · ${q.trip_type.replace("_", " ")}` : ""}`} />
      {q.legs.map((l, i) => <Row key={i} k={`${l.from} → ${l.to}`} v={`${l.miles_actual}→${l.miles_charged} mi × $${l.rate} = ${money(l.amount)}`} />)}
      <Row k="Mileage (rounded down)" v={q.mileage_raw != null ? `${money(q.mileage_raw)} → ${money(q.mileage_subtotal)}` : money(q.mileage_subtotal)} />
      <Row k={`Base fee · farthest ${q.farthest?.name ?? ""} ${q.farthest?.miles ?? ""} mi`} v={money(q.base_fee ?? q.base_tier?.base)} />
      {(q.waits || []).map((w, i) => <Row key={i} k={`Wait at ${w.at} · ${w.minutes} min`} v={money(w.fee)} />)}
      <Row k="Airport component" v={q.airport.applied ? `+${money(q.airport.amount)} (${q.airport.code})` : "$0.00"} />
      <Row k="Recurring discount" v={rec.applied ? `-${money(rec.discount)} · ${rec.pct}% · week ${rec.week} total ${money(rec.week_total)}` : "$0.00"} />
      <Row k="Booking fee" v="$0.00" />
      <Row k="Final fare" v={money(q.final_fare)} />
    </>
  );
}

export function RideDetailsDialog({ rideId, onClose }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["adminRide", rideId], queryFn: () => api.get(`/admin/rides/${rideId}`).then((r) => r.data) });
  const r = data?.ride;
  const setPayment = async (payment_status) => {
    try {
      await api.patch(`/admin/rides/${rideId}/payment`, { payment_status });
      toast.success("Payment status updated");
      qc.invalidateQueries({ queryKey: ["adminRide", rideId] });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-card" data-testid="ride-details-dialog">
        <DialogHeader><DialogTitle>Ride details</DialogTitle></DialogHeader>
        {r && (
          <div className="space-y-4">
            <div className="flex items-center justify-between"><StatusBadge status={r.status} /><span className="font-display text-2xl font-bold neon-text-lime">{money(r.fare)}</span></div>
            <Stops ride={r} />
            <div className="space-y-1">
              <Row k="Rider" v={`${r.rider_name}${r.rider_phone ? ` · ${r.rider_phone}` : ""}`} />
              <Row k="Driver / vehicle" v={r.driver_name ? `${r.driver_name} · ${r.driver_vehicle || ""}` : null} />
              <Row k="Ride type · passengers" v={`${RIDE_TYPES[r.ride_type] || r.ride_type} · ${r.passengers}`} />
              {[r.pickup, ...r.stops, r.dropoff].map((p, i) => <Row key={i} k={p.name} v={`${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`} />)}
              <Row k="Route distance / duration" v={`${r.distance_miles ?? "—"} mi · ${r.duration_min ?? "—"} min`} />
              <Row k="Created" v={`${when(r.created_at)} by ${r.created_by || "rider"}`} />
              <Row k="Approved" v={r.approved_at && `${when(r.approved_at)} · ${r.approved_by}`} />
              <Row k="Assigned" v={r.assigned_at && `${when(r.assigned_at)} · ${r.assigned_by}`} />
              <Row k="Completed" v={r.completed_at && when(r.completed_at)} />
              {r.cancellation_reason && <Row k="Cancelled" v={`${r.cancellation_reason} · ${r.cancelled_by}`} />}
              {r.decline_reason && <Row k="Declined" v={r.decline_reason} />}
              {r.notes && <Row k="Notes" v={r.notes} />}
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Payment status</span>
                <select value={r.payment_status} onChange={(e) => setPayment(e.target.value)} className="rounded border bg-background px-2 py-1 text-xs" data-testid="payment-status-select">
                  {PAYMENT.map((p) => <option key={p} value={p}>{p.toUpperCase()}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-1 border-t pt-3"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pricing</p><Pricing q={r.quote} /></div>
            <div className="space-y-1 border-t pt-3" data-testid="ride-audit-list">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Audit history</p>
              {data.audit.map((a, i) => <Row key={i} k={a.action.replace(/_/g, " ")} v={`${when(a.at)} · ${a.actor_email}${a.note ? ` · ${a.note}` : ""}`} />)}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
